import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { gmNamesFromConfig, ownNameFromConfig } from "../utils/users";
import { loadGmHudConfig } from "../utils/config";

const GAMES_CLOUD_BASE = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
    .replace(/^ws/, "http")
    .replace(/ws$/, "");

// How often the pending count is refreshed in the background. Kept deliberately slow so we don't
// hammer games-cloud / booking-calendar (each poll = one all-rooms read + one sheet read). Polling
// pauses when the tab is hidden and only refetches on return if the cache is older than this.
const POLL_MS = 90_000;
// Only the COUNT is cached in sessionStorage (no customer data) — enough for an instant badge on load.
const CACHE_KEY = "pendingReportsCount";

// Maps a room location to the gm-hud-config location key for per-user gating (matches ReportFlow).
function locationKey(loc) {
    const l = (loc || "").toLowerCase();
    if (l.includes("gothersgade")) return "GOTHERSGADE";
    if (l.includes("vridsl")) return "VRIDSLOSE";
    return null;
}

const Ctx = createContext({
    games: [], count: 0, loading: false, error: false,
    gmNames: [], ownName: "",
    refresh: () => {}, removeGame: () => {},
});

// PendingReportsProvider owns the single source of truth for "games awaiting a report": one shared
// fetch + timer, gated to the GM's allowed locations, used by both the Topbar badge and the report
// list. This keeps request volume low (one poller for the whole app, not one per component/page).
export function PendingReportsProvider({ children }) {
    const [games, setGames] = useState([]);
    const [count, setCount] = useState(() => {
        const c = parseInt(sessionStorage.getItem(CACHE_KEY), 10);
        return Number.isFinite(c) ? c : 0;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    // Names of everyone with system access, for the GM onsite / GM remote / Vært pickers.
    const [gmNames, setGmNames] = useState([]);
    const [ownName, setOwnName] = useState("");
    const configRef = useRef(null);
    const lastFetch = useRef(0);

    const applyCount = (n) => {
        setCount(n);
        try { sessionStorage.setItem(CACHE_KEY, String(n)); } catch { /* ignore */ }
    };

    const gate = (all) => {
        const config = configRef.current;
        if (!config) return all;
        const email = localStorage.getItem("userEmail") || "";
        const allowed = config.users?.[email]?.locations || [];
        return all.filter(g => {
            const key = locationKey(g.location);
            return key === null || allowed.includes(key);
        });
    };

    const refresh = useCallback(async () => {
        const token = localStorage.getItem("accessToken");
        if (!token || !GAMES_CLOUD_BASE) return;
        setLoading(true);
        setError(false);
        try {
            // Plain fetch (not fetchWithAuth) so a background poll never force-redirects on a stale
            // token — it just quietly keeps the last value.
            const res = await fetch(`${GAMES_CLOUD_BASE}pending-reports`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) { setError(true); return; }
            const data = await res.json();
            const g = gate(data.games || []);
            lastFetch.current = Date.now();
            setGames(g);
            applyCount(g.length);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    // Optimistic removal after a report is submitted — keeps badge + list instantly in sync.
    const removeGame = useCallback((eventId) => {
        setGames(prev => {
            const next = prev.filter(g => g.event_id !== eventId);
            applyCount(next.length);
            return next;
        });
    }, []);

    // Load the location-gating config once, then do the first fetch.
    useEffect(() => {
        let cancelled = false;
        loadGmHudConfig().then(cfg => {
            if (cancelled) return;
            configRef.current = cfg;
            setGmNames(gmNamesFromConfig(cfg));
            setOwnName(ownNameFromConfig(cfg));
            refresh();
        });
        return () => { cancelled = true; };
    }, [refresh]);

    // Background poll while the tab is visible; refetch on return if the cache went stale.
    useEffect(() => {
        const id = setInterval(() => {
            if (document.visibilityState === "visible") refresh();
        }, POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible" && Date.now() - lastFetch.current > POLL_MS) refresh();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [refresh]);

    return (
        <Ctx.Provider value={{ games, count, loading, error, gmNames, ownName, refresh, removeGame }}>
            {children}
        </Ctx.Provider>
    );
}

export const usePendingReports = () => useContext(Ctx);
